
import { format } from "date-fns";
import type { Locale } from 'date-fns';
import { enUS, fr, es, it, de, ru, sv, pt, zhCN, ar } from 'date-fns/locale';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";

interface WeatherData {
  date?: string;
  Date?: string;
  temperature?: {
    min: number;
    max: number;
  };
  Temperature?: {
    Minimum: {
      Value: number;
      Unit: string;
    };
    Maximum: {
      Value: number;
      Unit: string;
    };
  };
  description?: string;
  weatherCode?: number;
  Day?: {
    Icon: number;
    IconPhrase: string;
    IconUrl?: string;
  };
}

interface WeatherCarouselProps {
  weatherData: WeatherData[];
  cityName: string;
}

const locales: { [key: string]: Locale } = {
  en: enUS,
  fr,
  es,
  it,
  de,
  ru,
  sv,
  pt,
  zh: zhCN,
  ar
};


export function WeatherCarousel({ weatherData, cityName }: WeatherCarouselProps) {
  const { i18n, t } = useTranslation();
  const locale = locales[i18n.language] || enUS;

  return (
    <Carousel opts={{ loop: true }} className="w-full max-w-xs mx-auto">
      <CarouselContent>
        {weatherData.map((day, index) => {
          // Handle both old and new API response formats
          const date = day.Date || day.date || '';
          const minTemp = day.Temperature?.Minimum.Value !== undefined ? day.Temperature.Minimum.Value : day.temperature?.min;
          const maxTemp = day.Temperature?.Maximum.Value !== undefined ? day.Temperature.Maximum.Value : day.temperature?.max;
          const unit = day.Temperature?.Minimum.Unit || 'C';
          const description = day.Day?.IconPhrase || day.description || '';
          const weatherIconUrl = day.Day?.IconUrl || '';
          const iconUrl = day.Day?.IconUrl;
          
          return (
            <CarouselItem key={date + index}>
              <Card className="p-4">
                <CardContent className="flex flex-col items-center space-y-4">
                  <div className="text-lg font-semibold">
                    {format(new Date(date), 'EEEE, MMM d', { locale })}
                  </div>
                  <img 
                      src={weatherIconUrl} 
                      alt={description}
                      className="w-18 h-12"
                    />
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-4 text-2xl">
                      <span className="font-bold">{Math.round(maxTemp || 0)}°{unit}</span>
                      <span className="text-muted-foreground">{Math.round(minTemp || 0)}°{unit}</span>
                    </div>
                    <div className="flex justify-center gap-2 text-xs text-muted-foreground mt-1">
                      <span>{t('weather.max')}</span> / <span>{t('weather.min')}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{description}</p>
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className="hidden sm:flex" />
      <CarouselNext className="hidden sm:flex" />
    </Carousel>
  );
}
